package fr.frux.core.common.error

/**
 * Categorie d'echec presentable a l'utilisateur (#122).
 *
 * Le detail technique — « Fail to fetch », « Connection refused », une trace de deserialisation —
 * ne remonte jamais a l'ecran : il reste dans la cause, donc dans les logs. Ce que l'interface
 * recoit est une categorie, et d'elle seule decoule le libelle affiche.
 *
 * C'est une [Exception] et non un type de retour distinct pour tenir dans les `Result<T>` deja
 * en place : les repositories echouent avec un `AppError` au lieu de l'exception de transport
 * brute, sans changer une signature.
 */
sealed class AppError(
    cause: Throwable?,
) : Exception(cause?.message, cause) {
    /** Serveur injoignable, DNS, timeout, coupure reseau — tout ce qui n'a pas atteint l'API. */
    class Network(
        cause: Throwable? = null,
    ) : AppError(cause)

    /** 401 : jeton absent, expire, ou identifiants refuses. Le sens depend de l'appelant. */
    class Unauthorized(
        cause: Throwable? = null,
    ) : AppError(cause)

    /** 404 : la ressource visee n'existe pas (ou plus). */
    class NotFound(
        cause: Throwable? = null,
    ) : AppError(cause)

    /**
     * 409 : conflit d'etat — deja reagi, nom d'utilisateur pris.
     *
     * [userMessageOverride] permet a un appelant qui a distingue une cause precise (ex. `deleteTree`
     * sur un arbre portant une recolte active, #224) de porter un libelle specifique plutot que le
     * message generique du conflit — la solution la plus simple qui n'ajoute pas un type d'erreur
     * dedie pour un seul cas d'usage. Seule chaine utilisateur qui traverse encore cette couche :
     * c'est une regle metier deja formulee par le serveur, il n'y a pas de categorie a traduire
     * derriere (#176). Le mapping des categories vit en couche UI (`core-designsystem`).
     */
    class Conflict(
        cause: Throwable? = null,
        val userMessageOverride: String? = null,
        /** Cause métier reconnue par son code stable (lieux, ADR 0035), `null` pour un conflit générique. */
        val reason: ConflictReason? = null,
    ) : AppError(cause)

    /** 4xx de saisie (hors 401/404/409/429) : la requete est refusee pour son contenu. */
    class Invalid(
        cause: Throwable? = null,
        /**
         * Raison serveur reconnue, quand le corps `ErrorResponse` en porte une que l'UI sait
         * traduire mieux que le libelle generique (#341). Reste `null` dans l'immense majorite des
         * cas : ce n'est pas un canal de messages serveur, seulement une poignee de codes stables.
         */
        val reason: InvalidReason? = null,
    ) : AppError(cause)

    /**
     * 429 : quota depasse (#24). Categorie a part et non un [Invalid], parce que la conduite a
     * tenir est l'inverse — ne rien changer a la saisie, attendre puis reessayer.
     *
     * [retryAfterSeconds] vaut null si le serveur n'a pas envoye de `Retry-After` : le message
     * reste alors vague plutot que d'annoncer un delai invente.
     */
    class RateLimited(
        val retryAfterSeconds: Long? = null,
        cause: Throwable? = null,
        /**
         * 429 de budget/quota journalier (#291) et non de rythme : `OTP_BUDGET_EXHAUSTED`,
         * `AUTHOR_PHOTO_QUOTA`, `REPORT_QUOTA_EXCEEDED`. « Patiente un instant » serait un
         * mensonge, la fenêtre ne se recharge que le lendemain.
         */
        val quotaExhausted: Boolean = false,
    ) : AppError(cause)

    /** 5xx : l'API a repondu, mal. Rien que l'utilisateur puisse corriger. */
    class Server(
        val status: Int? = null,
        cause: Throwable? = null,
    ) : AppError(cause)

    /** Tout le reste, y compris un bug client : jamais montre tel quel. */
    class Unexpected(
        cause: Throwable? = null,
    ) : AppError(cause)
}

/**
 * Normalise n'importe quel echec en categorie presentable. Un `Throwable` qui n'est pas un
 * [AppError] n'a pas traverse le mapping du reseau : c'est un imprevu, et son message brut ne
 * s'affichera jamais (le libelle vient du seul type, cf. `AppError.userMessage()` en couche UI).
 *
 * C'est le point de passage obligatoire avant de poser un echec dans un `UiState` : les etats
 * portent des `AppError`, pas des `Throwable` ni des chaines (#176).
 */
fun Throwable.asAppError(): AppError = this as? AppError ?: AppError.Unexpected(this)

/**
 * Vrai pour un echec attendu du flux normal (reseau coupe, saisie refusee, quota, conflit…) :
 * l'UI l'affiche et l'utilisateur peut agir, ce n'est pas un defaut du client. A logger sous le
 * niveau erreur — le pont Logger.e → Sentry (#175) transformerait chaque 400 metier en event
 * (#310). Seul [AppError.Unexpected] (et tout Throwable hors mapping) reste une erreur a capturer.
 */
val Throwable.isExpectedFailure: Boolean
    get() = this is AppError && this !is AppError.Unexpected

/**
 * Raisons serveur reconnues par l'UI (#341). Une valeur ici est un engagement de contrat : le code
 * correspondant vit dans `ErrorResponse.code` cote serveur, et son libelle dans `AppErrorMessages`.
 */
enum class InvalidReason {
    /** 403 `ACCOUNT_SUSPENDED` : le compte est ferme par l'operateur (ADR 0023 lot 3). Le motif
     *  n'est jamais transmis, la voie de recours est contact@frux-app.fr. */
    ACCOUNT_SUSPENDED,

    /** 403 `PREMIUM_REQUIRED` : la route est réservée à Frux+ (#456, ADR 0016). */
    PREMIUM_REQUIRED,

    /** 400 `MOVE_TOO_FAR` : la position corrigée dépasse la distance permise (50 m). */
    MOVE_TOO_FAR,

    /** 403 `ATTACH_FORBIDDEN` : le lieu est privé et l'appelant n'en est pas propriétaire. */
    ATTACH_FORBIDDEN,

    /** 403 `SELF_REPORT` : on ne signale pas son propre lieu. */
    SELF_REPORT,

    /** 403 `SITE_PRIVATE` : on ne lance pas de récolte sur un lieu privé dont on n'est pas propriétaire. */
    SITE_PRIVATE,

    /** 403 `NOT_SITE_OWNER` : seul le propriétaire déclaré peut inviter chez lui. */
    NOT_SITE_OWNER,
}

/** Causes de 409 reconnues sur les lieux de cueillette (ADR 0035), libellé dans `AppErrorMessages`. */
enum class ConflictReason {
    TREE_ALREADY_ATTACHED,
    ATTACH_TOO_FAR,
    PLANTING_EXISTS,
    ALREADY_REPORTED,
}

/** Vrai pour le refus d'une fonction réservée à Frux+ (403 `PREMIUM_REQUIRED`, #456). */
fun Throwable.isPremiumRequired(): Boolean = this is AppError.Invalid && reason == InvalidReason.PREMIUM_REQUIRED
